import type { RouteObject } from 'react-router-dom';
import { retryImport } from '@doxyme/ui/utils';
import { checkInRoomLoader } from './loaders/checkInRoomLoader';
import { lazyPage } from './withPageLayout';
import { getLogger } from '../libs/logger';
import { RouteHydrateFallback } from './hydrateFallback';

const logger = getLogger('routeLoader');

/**
 * Retries a route-level dynamic `import()` with the shared backoff policy and
 * emits DataDog telemetry, mirroring the resilience `lazyPage` gives page
 * routes. Used for the layout/error-boundary chunks loaded directly by the
 * root and `iframe-in-iframe` route loaders.
 */
const retryRouteImport = <T,>(
  factory: () => Promise<T>,
  chunkName: string,
): Promise<T> =>
  retryImport(factory, {
    onSuccessAfterRetry: ({ attempts }) =>
      logger.info('route import succeeded after retry', {
        chunkName,
        attempts,
      }),
    onFinalFailure: ({ attempts, error }) =>
      logger.error('route import failed permanently', {
        chunkName,
        attempts,
        error,
      }),
  });

/**
 * React Router 7 route tree for `apps/frontend`, mounted under the `/v2`
 * basename by `main.tsx`.
 *
 * Each route reuses its existing page module via `lazyPage` (which re-applies
 * the page's `getLayout`), so behaviour and layout nesting are preserved.
 * Splat (`*`) routes mirror production nginx slug rewrites; destination pages
 * parse the slug/category from the pathname (`useAppsRoute`, `useAppsSettingsSlug`,
 * `useCheckInRoom`).
 *
 * Routing structure: a pathless `RootLayout` route hosts all app-wide providers
 * and chrome and renders pages via `<Outlet>`. `iframe-in-iframe` sits outside
 * it with a minimal layout (no providers/auth).
 *
 * NOTE: the waiting-room slug route shape is provisional — `usePatientWaitingRoom`
 * reads `?slug=` from `window.location.search`, so prod edge slug behaviour must
 * be confirmed before this is locked (see PROD-8478 known risks).
 */
const appRoutes: RouteObject[] = [
  { id: 'index', index: true, lazy: lazyPage(() => import('../pages/index')) },

  // Provider auth + entry flows
  {
    id: 'sign-in',
    path: 'sign-in',
    lazy: lazyPage(() => import('../pages/sign-in')),
  },
  {
    id: 'sign-in-slug',
    path: 'sign-in/:slug',
    lazy: lazyPage(() => import('../pages/sign-in/[slug]')),
  },
  {
    id: 'provider-selector',
    path: 'provider-selector',
    lazy: lazyPage(() => import('../pages/provider-selector')),
  },
  {
    id: 'onboarding',
    path: 'onboarding',
    lazy: lazyPage(() => import('../pages/onboarding')),
  },
  {
    id: 'post-login',
    path: 'post-login',
    lazy: lazyPage(() => import('../pages/post-login')),
  },
  {
    id: 'logout',
    path: 'logout',
    lazy: lazyPage(() => import('../pages/logout')),
  },
  {
    id: 'select-workspace',
    path: 'select-workspace',
    lazy: lazyPage(() => import('../pages/select-workspace')),
  },
  {
    id: 'forgot-password',
    path: 'forgot-password',
    lazy: lazyPage(() => import('../pages/forgot-password')),
  },
  {
    id: 'password-reset',
    path: 'password-reset',
    lazy: lazyPage(() => import('../pages/password-reset')),
  },
  {
    id: 'reset-password',
    path: 'reset-password',
    lazy: lazyPage(() => import('../pages/reset-password')),
  },
  {
    id: 'check-your-email',
    path: 'check-your-email',
    lazy: lazyPage(() => import('../pages/check-your-email')),
  },
  {
    id: 'order-success',
    path: 'order/success',
    lazy: lazyPage(() => import('../pages/order/success')),
  },
  {
    id: 'wrong-room',
    path: 'wrong-room',
    lazy: lazyPage(() => import('../pages/wrong-room')),
  },
  {
    id: 'wrong-clinic',
    path: 'wrong-clinic',
    lazy: lazyPage(() => import('../pages/wrong-clinic')),
  },

  // Patient flows (check-in / waiting-room)
  {
    id: 'check-in',
    path: 'check-in',
    loader: checkInRoomLoader,
    lazy: lazyPage(() => import('../pages/check-in')),
  },
  {
    id: 'check-in-splat',
    path: 'check-in/*',
    loader: checkInRoomLoader,
    lazy: lazyPage(() => import('../pages/check-in')),
  },
  {
    id: 'waiting-room',
    path: 'waiting-room',
    lazy: lazyPage(() => import('../pages/waiting-room')),
  },
  {
    id: 'waiting-room-slug',
    path: 'waiting-room/:slug',
    lazy: lazyPage(() => import('../pages/waiting-room')),
  },

  // Call flows
  { id: 'call', path: 'call', lazy: lazyPage(() => import('../pages/call')) },
  {
    id: 'call-hold',
    path: 'call/hold',
    lazy: lazyPage(() => import('../pages/call/hold')),
  },
  {
    id: 'call-test-call',
    path: 'call/test-call',
    lazy: lazyPage(() => import('../pages/call/test-call')),
  },

  // Account
  {
    id: 'account-dashboard',
    path: 'account/dashboard',
    lazy: lazyPage(() => import('../pages/account/dashboard')),
  },
  {
    id: 'account-analytics',
    path: 'account/analytics',
    lazy: lazyPage(() => import('../pages/account/analytics')),
  },
  {
    id: 'account-meeting-history',
    path: 'account/meeting-history',
    lazy: lazyPage(() => import('../pages/account/meeting-history')),
  },
  {
    id: 'account-app',
    path: 'account/app',
    lazy: lazyPage(() => import('../pages/account/app')),
  },
  {
    id: 'account-apps',
    path: 'account/apps',
    lazy: lazyPage(() => import('../pages/account/apps')),
  },
  {
    id: 'account-apps-splat',
    path: 'account/apps/*',
    lazy: lazyPage(() => import('../pages/account/apps')),
  },
  {
    id: 'account-not-available',
    path: 'account/not-available',
    lazy: lazyPage(() => import('../pages/account/not-available')),
  },

  // Account settings
  {
    id: 'account-settings',
    path: 'account/settings',
    lazy: lazyPage(() => import('../pages/account/settings')),
  },
  {
    id: 'account-settings-account',
    path: 'account/settings/account',
    lazy: lazyPage(() => import('../pages/account/settings/account')),
  },
  {
    id: 'account-settings-profile',
    path: 'account/settings/profile',
    lazy: lazyPage(() => import('../pages/account/settings/profile')),
  },
  {
    id: 'account-settings-preferences',
    path: 'account/settings/preferences',
    lazy: lazyPage(() => import('../pages/account/settings/preferences')),
  },
  {
    id: 'account-settings-baa',
    path: 'account/settings/baa',
    lazy: lazyPage(() => import('../pages/account/settings/baa')),
  },
  {
    id: 'account-settings-users',
    path: 'account/settings/users',
    lazy: lazyPage(() => import('../pages/account/settings/users')),
  },
  {
    id: 'account-settings-rooms',
    path: 'account/settings/rooms',
    lazy: lazyPage(() => import('../pages/account/settings/rooms')),
  },
  {
    id: 'account-settings-clinic',
    path: 'account/settings/clinic',
    lazy: lazyPage(() => import('../pages/account/settings/clinic')),
  },
  {
    id: 'account-settings-subscription',
    path: 'account/settings/subscription',
    lazy: lazyPage(() => import('../pages/account/settings/subscription')),
  },
  {
    id: 'account-settings-branding',
    path: 'account/settings/branding',
    lazy: lazyPage(() => import('../pages/account/settings/branding')),
  },
  {
    id: 'account-settings-branding-edit',
    path: 'account/settings/branding/edit',
    lazy: lazyPage(() => import('../pages/account/settings/branding/edit')),
  },
  {
    id: 'account-settings-apps',
    path: 'account/settings/apps',
    lazy: lazyPage(() => import('../pages/account/settings/apps')),
  },
  {
    id: 'account-settings-apps-splat',
    path: 'account/settings/apps/*',
    lazy: lazyPage(() => import('../pages/account/settings/apps')),
  },
  {
    id: 'account-settings-room-edit',
    path: 'account/settings/room/edit',
    lazy: lazyPage(() => import('../pages/account/settings/room/edit')),
  },
  {
    id: 'account-settings-room-edit-roomId',
    path: 'account/settings/room/edit/:roomId',
    lazy: lazyPage(
      () => import('../pages/account/settings/room/edit/[roomId]'),
    ),
  },

  // Frontegg auth routes — RR7 placeholder only. Frontegg renders from the browser
  // URL inside `FronteggProvider`.
  {
    id: 'frontegg-auth',
    path: 'auth/*',
    Component: () => null,
  },

  // Catch-all for unmatched URLs — must stay last so it does not shadow
  // specific routes. Reuses the wrong-room page so unknown paths show app UI
  // instead of React Router's default error screen.
  {
    id: 'not-found',
    path: '*',
    lazy: lazyPage(() => import('../pages/wrong-room')),
  },
];

export const routes: RouteObject[] = [
  {
    id: 'iframe-in-iframe',
    path: 'iframe-in-iframe',
    lazy: async () => {
      const [{ default: Page }, { IframeLayout }, { RouteErrorBoundary }] =
        await Promise.all([
          retryRouteImport(
            () => import('../pages/iframe-in-iframe'),
            'iframe-in-iframe',
          ),
          retryRouteImport(() => import('./iframeLayout'), 'iframeLayout'),
          retryRouteImport(
            () => import('./routeErrorBoundary'),
            'routeErrorBoundary',
          ),
        ]);
      return {
        Component: () => (
          <IframeLayout>
            <Page />
          </IframeLayout>
        ),
        ErrorBoundary: RouteErrorBoundary,
      };
    },
  },
  {
    id: 'root',
    // Statically defined (not via `lazy`) so it is available synchronously when
    // a user lands directly on a child route with a loader (e.g. check-in). RR7
    // renders this while the loader runs and the lazy layout chunk resolves on
    // initial hydration; child routes without their own fallback inherit it.
    HydrateFallback: RouteHydrateFallback,
    lazy: async () => {
      const [{ RootLayout }, { RouteErrorBoundary }] = await Promise.all([
        retryRouteImport(() => import('./rootLayout'), 'rootLayout'),
        retryRouteImport(
          () => import('./routeErrorBoundary'),
          'routeErrorBoundary',
        ),
      ]);
      return { Component: RootLayout, ErrorBoundary: RouteErrorBoundary };
    },
    children: appRoutes,
  },
];
